import { createSlice, type PayloadAction } from '@reduxjs/toolkit';

export interface BasketServiceArticle {
  id: string;
  type: string;
  time?: string;
  selected: boolean;
}

interface BasketItemCommon {
  modelCode: string;
  name: string;
  imageUrl?: string;
  natureId: number;
  natureTitle: string;
  quantity: number;
  relatedItems: string[];
}
export interface ServiceBasketItem extends BasketItemCommon {
  type: 'service';
  articles: BasketServiceArticle[];
}

export interface SparePartBasketItem extends BasketItemCommon {
  type: 'sparePart';
  selectedArticleId: string;
}

export type BasketItem = SparePartBasketItem | ServiceBasketItem;

interface BasketState {
  items: BasketItem[];
}

type UpsertBasketItemPayload = {
  item: Omit<SparePartBasketItem, 'relatedItems'> | Omit<ServiceBasketItem, 'relatedItems'>;
  relatedTo?: string;
};

const initialState: BasketState = {
  items: [],
};

const findItemIndex = (items: BasketItem[], type: BasketItem['type'], modelCode: string) =>
  items.findIndex(item => item.type === type && item.modelCode === modelCode);

/* Adds a related model code without duplicating an already existing relation */
const addRelatedItem = (relatedItems: string[], relatedModelCode: string) =>
  relatedItems.includes(relatedModelCode) ? relatedItems : [...relatedItems, relatedModelCode];

const basketSlice = createSlice({
  name: 'basket',
  initialState,
  extraReducers: builder => builder.addCase('resetStore', () => initialState),
  reducers: {
    upsertBasketItem: (state, action: PayloadAction<UpsertBasketItemPayload>) => {
      const { item, relatedTo } = action.payload;
      const index = findItemIndex(state.items, item.type, item.modelCode);

      /* Insert or update the item */
      if (index === -1) {
        state.items.push({ ...item, relatedItems: relatedTo ? [relatedTo] : [] });
      } else {
        state.items[index] = {
          ...state.items[index],
          ...item,
          relatedItems: relatedTo ? addRelatedItem(state.items[index].relatedItems, relatedTo) : state.items[index].relatedItems,
        };
      }

      /* Bi-directional relation */
      if (relatedTo) {
        const relatedIndex = state.items.findIndex(i => i.modelCode === relatedTo);

        if (relatedIndex !== -1) {
          state.items[relatedIndex].relatedItems = addRelatedItem(state.items[relatedIndex].relatedItems, item.modelCode);
        }
      }
    },
    removeBasketItem: (state, action: PayloadAction<{ type: BasketItem['type']; modelCode: string }>) => {
      const { type, modelCode } = action.payload;
      const index = findItemIndex(state.items, type, modelCode);

      if (index === -1) return;

      state.items.splice(index, 1);

      /* Remove the item from related items */
      state.items.forEach(item => {
        item.relatedItems = item.relatedItems.filter(relatedModelCode => relatedModelCode !== modelCode);
      });
    },
    clearBasket: () => initialState,
  },
});

export const {
  reducer: basketReducer,
  actions: { upsertBasketItem, removeBasketItem, clearBasket },
} = basketSlice;
